import _ from 'lodash';
import { queryClient } from 'App';
import { getRecoil, setRecoil } from 'recoil-nexus';

import state from 'components/state';
import {
  abortedEnhanceFormsState,
  enhanceAbortingState,
  enhanceNoteLoadingState
} from 'components/practice/charts/ClinicalNote/MyScribeAI/state';

import { dropOtherNoteResponse, getResponseNoteId } from './otherNoteResponse';

const defaultClinicalNote = {
  notes: '',
  subjective: '',
  objective: '',
  present_illness: '',
  other_complaints: '',
  assessment: '',
  plan: '',
  exam: '',
  test: '',
  epn: {
    riskFactors: {
      narrative: ''
    },
    session: {
      narrative: ''
    },
    sessionQuotesAndContents: '',
    activeSymptoms: {
      narrative: ''
    },
    functionalImpairment: {
      narrative: ''
    },
    interventions: {
      narrative: ''
    },
    progress: {
      narrative: ''
    }
  },
  goals: '',
  goals_narrative: '',
  mha: {
    riskAndCoMorbidities: '',
    timeFrames: ''
  },
  hpi: {
    narrative: ''
  },
  ros: {
    narrative: ''
  }
};

const updateClinicalNote = (data) => {
  try {
    if (data?.response === undefined && data?.task_id) {
      setRecoil(state.intelligenceAiState, (ps) => ({
        ...ps,
        activeTaskIds: [...ps.activeTaskIds, data?.task_id]
      }));
    } else {
      const {
        vertical,
        form_name: initialFormName,
        form_type,
        response,
        task_id,
        destName,
        destId,
        abort = false,
        done = false
      } = data;

      if (!abort) {
        const abortedForms = getRecoil(abortedEnhanceFormsState);
        if (abortedForms?.includes?.(form_type)) {
          const currentIds = getRecoil(state.intelligenceAiState)?.activeTaskIds || [];
          const remaining = currentIds.filter((id) => id !== task_id);
          setRecoil(state.intelligenceAiState, (ps) => {
            const nextResponses = { ...(ps.responses || {}) };
            Object.keys(nextResponses).forEach((key) => {
              if (form_type && key.slice(key.lastIndexOf('.') + 1) === form_type)
                delete nextResponses[key];
            });
            return {
              ...ps,
              activeTaskIds: remaining,
              loading: remaining.length > 0,
              responses: nextResponses
            };
          });
          setRecoil(enhanceNoteLoadingState, (prev) => prev.filter((n) => n !== form_type));
          if (remaining.length === 0) {
            setRecoil(enhanceAbortingState, false);
            setRecoil(abortedEnhanceFormsState, []);
          }
          return;
        }

        const currentAiState = getRecoil(state.intelligenceAiState);
        if (!currentAiState?.activeTaskIds?.includes?.(task_id)) {
          return;
        }

        const responseNoteId = getResponseNoteId({ destName, destId });
        if (dropOtherNoteResponse({ responseNoteId, task_id, form_type, done })) return;

        const isNotCustomForm = !form_type?.includes('custom_form_narrative');
        let form_name = initialFormName;

        if (form_name === 'progress') {
          form_name = 'epn';
        }
        if (form_name === 'intake') {
          form_name = 'mha';
        }

        const isLocked = !!getRecoil(state.clinicalNote)?.locked;

        if (
          !isLocked &&
          ['soap', 'hp', 'clinical_narrative'].includes(vertical) &&
          isNotCustomForm
        ) {
          if (defaultClinicalNote?.[form_name]?.[form_type]?.['narrative'] !== undefined) {
            setRecoil(state.clinicalNote, (ps) => ({
              ...ps,
              [form_name]: {
                ...ps?.[form_name],
                [form_type]: {
                  ...ps?.[form_name]?.[form_type],
                  narrative: response
                }
              }
            }));
          } else if (defaultClinicalNote?.[form_name]?.[form_type] !== undefined) {
            setRecoil(state.clinicalNote, (ps) => ({
              ...ps,
              [form_name]: {
                ...ps?.[form_name],
                [form_type]: response
              }
            }));
          } else if (defaultClinicalNote?.[form_type]?.['narrative'] !== undefined) {
            setRecoil(state.clinicalNote, (ps) => ({
              ...ps,
              [form_type]: {
                ...ps?.[form_type],
                narrative: response
              }
            }));
          } else if (defaultClinicalNote?.[form_type] !== undefined) {
            setRecoil(state.clinicalNote, (ps) => ({
              ...ps,
              [form_type]: response
            }));
          }
        }

        const newAiClinicalNoteState = getRecoil(state.intelligenceAiState);
        if (newAiClinicalNoteState?.timeoutTimeout)
          clearTimeout(newAiClinicalNoteState?.timeoutTimeout);

        const responsePath = `${vertical}.${data?.form_name}.${data?.form_type}`;

        setRecoil(state.intelligenceAiState, (ps) => {
          let newTaskIds = ps.activeTaskIds;

          if (!!done) {
            const index = newTaskIds.findIndex((item) => item === task_id);
            if (index > -1) {
              const newValue = [...newTaskIds];
              newValue.splice(index, 1);
              newTaskIds = newValue;
            }
          }

          const prevResponses =
            String(ps.noteId) === String(responseNoteId) ? ps.responses || {} : {};

          const isKeptResponse = !isNotCustomForm && responseNoteId != null;

          return {
            ...ps,
            activeTaskIds: newTaskIds,
            loading: !!!done,
            response: data?.response,
            path: responsePath,
            noteId: responseNoteId,
            responses: isKeptResponse
              ? { ...prevResponses, [responsePath]: data?.response }
              : prevResponses
          };
        });

        if (!!data?.done) {
          setRecoil(enhanceNoteLoadingState, (prev) => {
            const index = prev.findIndex((item) => item === form_type);
            if (index === -1) return prev;

            const newValue = [...prev];
            newValue.splice(index, 1);
            return newValue;
          });

          if (!isNotCustomForm && form_type) {
            const customFormId = form_type.slice(form_type.indexOf(':') + 1);
            queryClient.setQueriesData(
              {
                predicate: (query) =>
                  query.queryKey?.[0] === 'customFormsById' &&
                  String(query.queryKey?.[1]) === String(customFormId)
              },
              (old) =>
                old?.data?.formResponse
                  ? {
                      ...old,
                      data: {
                        ...old.data,
                        formResponse: { ...old.data.formResponse, narrative: response }
                      }
                    }
                  : old
            );
          }
        }
      } else {
        const currentIds = getRecoil(state.intelligenceAiState)?.activeTaskIds || [];
        const wasActive = currentIds.includes(task_id);
        const remaining = wasActive ? currentIds.filter((id) => id !== task_id) : currentIds;

        if (wasActive) {
          setRecoil(state.intelligenceAiState, (ps) => {
            const nextResponses = { ...(ps.responses || {}) };
            Object.keys(nextResponses).forEach((key) => {
              if (form_type && key.slice(key.lastIndexOf('.') + 1) === form_type)
                delete nextResponses[key];
            });
            return {
              ...ps,
              activeTaskIds: remaining,
              loading: remaining.length > 0,
              response: '',
              path: '',
              responses: nextResponses
            };
          });

          setRecoil(enhanceNoteLoadingState, (prev) => {
            const index = prev.findIndex((item) => item === form_type);
            if (index === -1) return prev;
            const next = [...prev];
            next.splice(index, 1);
            return next;
          });
        }

        if (remaining.length === 0) {
          setRecoil(enhanceAbortingState, false);
          setRecoil(abortedEnhanceFormsState, []);
        }
      }
    }
  } catch (e) {
    console.log(e);
  }
};

export default updateClinicalNote;
